import React from "react";
import ReactDOM from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import "@/index.css";
import "@/warehouseMovementHistory.css";
import App from "@/App";
import AppErrorBoundary from "@/components/AppErrorBoundary";
import { installTimezoneLocalePatch } from "@/lib/timezone";
import { installSafeStorageGuard } from "@/lib/safariCompat";

installSafeStorageGuard();
installTimezoneLocalePatch();

// Cache bakımı artık uygulama açılışını ASLA bloklamaz.
// Safari/WebKit bazı serviceWorker/CacheStorage çağrılarını bekletebildiği için
// React önce render edilir; eski kayıtların temizliği arka planda best-effort yapılır.
const CACHE_MAINTENANCE_VERSION = "v2.4.13";
const CACHE_MAINTENANCE_KEY = "ofisgorev_cache_maintenance_version";

function withTimeout(promise, timeoutMs = 1200) {
  return Promise.race([
    Promise.resolve(promise),
    new Promise((resolve) => window.setTimeout(resolve, timeoutMs)),
  ]);
}

function maintainBrowserCachesInBackground() {
  let completed = false;
  try {
    completed = window.localStorage.getItem(CACHE_MAINTENANCE_KEY) === CACHE_MAINTENANCE_VERSION;
  } catch {}
  if (completed) return;

  // Marker'ı işlemden önce yaz: Safari API'si takılsa bile sonraki açılış yeniden bloklanmaz.
  try { window.localStorage.setItem(CACHE_MAINTENANCE_KEY, CACHE_MAINTENANCE_VERSION); } catch {}

  window.setTimeout(async () => {
    try {
      if ("serviceWorker" in navigator) {
        const registrations = await withTimeout(navigator.serviceWorker.getRegistrations());
        if (Array.isArray(registrations)) {
          await withTimeout(Promise.allSettled(registrations.map((registration) => registration.unregister())));
        }
      }
    } catch {}

    try {
      if ("caches" in window) {
        const keys = await withTimeout(caches.keys());
        if (Array.isArray(keys)) {
          await withTimeout(Promise.allSettled(keys.map((key) => caches.delete(key))));
        }
      }
    } catch {}
  }, 0);
}

window.addEventListener("error", (e) => {
  if (e.message && e.message.includes("ResizeObserver loop")) {
    e.stopImmediatePropagation();
  }
}, true);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      refetchOnWindowFocus: false,
    },
  },
});

const rootElement = document.getElementById("root");
if (rootElement) {
  const root = ReactDOM.createRoot(rootElement);
  root.render(
    <React.StrictMode>
      <AppErrorBoundary>
        <QueryClientProvider client={queryClient}>
          <App />
        </QueryClientProvider>
      </AppErrorBoundary>
    </React.StrictMode>,
  );
}

maintainBrowserCachesInBackground();
